import React from 'react';
import { Divider, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { StyledCard } from 'components/Custom/card';
import { CardTitleSpan } from 'components/Custom/spans';
import { CardInfo } from './card-info';
import { Contact } from 'store/vat-checker/models';
import { ContactType } from 'store/app/enums';
import { getContactTypeLabel } from 'utils/display-enum-label';
import { getVIESProviderLink } from 'utils/get-provider-link';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';
import { Provider } from 'store/api/enums';
import ImgWithXeroSignIn from 'assets/images/xero_icon.png';
import ImgWithQBOSignIn from 'assets/images/quickbooks_icon.svg';
import ImgWithExcelSignIn from 'assets/images/excel_icon.svg';
import ImgWithZohoSignIn from 'assets/images/zoho_icon.png';
import { AiScanningLogo } from 'components/Custom/AiScanningLogo';
import { CloseOutlined, UsergroupAddOutlined } from '@ant-design/icons';

interface ContactDetailsCardProps {
  selectedContact: Contact | null;
  /** Defaults to the standard side-panel width. */
  width?: string;
  /** Optional extra styles for the card wrapper. */
  style?: React.CSSProperties;
  providerName?: string;
  shortCode?: string;
  windowWidth: number;
  windowHeight: number;
  ENTITY_DETAILS_BREAKPOINT: number;
  ENTITY_DETAILS_HEIGHT_BREAKPOINT: number;
  inlineBesideMain?: boolean;
  hasAttachments: boolean;
  /** When set (narrow side panel), renders a close control in the card header. */
  onClose?: () => void;
}

const renderProviderLogo = (provider?: string) => {
  const providerKey = provider?.toLowerCase();
  const alt = provider || '';

  switch (providerKey) {
    case 'group':
      return (
        <UsergroupAddOutlined
          style={{
            fontSize: 24,
            color: 'var(--color-on-logo-tile, #042a2a)',
            paddingTop: '4px',
          }}
        />
      );
    case Provider.QUICKBOOKS:
      return (
        <img
          src={ImgWithQBOSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.EXCEL:
      return (
        <img
          src={ImgWithExcelSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.ZOHO:
      return (
        <img
          src={ImgWithZohoSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
    case Provider.RECEIPTS:
      return <AiScanningLogo size={36} />;
    default:
      return (
        <img
          src={ImgWithXeroSignIn}
          alt={alt}
          style={{ width: '36px', height: '36px' }}
        />
      );
  }
};

export const ContactDetailsCard: React.FC<ContactDetailsCardProps> = ({
  selectedContact,
  style,
  providerName,
  shortCode,
  windowWidth,
  windowHeight,
  ENTITY_DETAILS_BREAKPOINT,
  ENTITY_DETAILS_HEIGHT_BREAKPOINT,
  inlineBesideMain = false,
  hasAttachments,
  onClose,
}) => {
  const { t } = useTranslation();

  const hasValue = (value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    return !!trimmed && trimmed !== 'null';
  };

  const renderOrDash = (value: string | null | undefined) => {
    const trimmed = (value ?? '').trim();
    return hasValue(trimmed) ? trimmed : '-';
  };

  const useWideLayout =
    windowWidth <= ENTITY_DETAILS_BREAKPOINT ||
    windowHeight <= ENTITY_DETAILS_HEIGHT_BREAKPOINT;

  const cardWidth =
    inlineBesideMain && !useWideLayout
      ? `${TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}px`
      : useWideLayout
        ? hasAttachments
          ? '1020px'
          : '680px'
        : '320px';

  const fieldsMaxWidth: React.CSSProperties['maxWidth'] = inlineBesideMain
    ? '100%'
    : useWideLayout
      ? hasAttachments
        ? 1020
        : 1000
      : 320;
  const formatContactType = (record: Contact) =>
    record.contactType
      ? getContactTypeLabel(record.contactType, t)
      : '';

  const providerContactHref = React.useMemo(() => {
    if (!selectedContact?.providerContactId) return undefined;
    const provider =
      (providerName ?? selectedContact.providerName)?.toLowerCase();
    const contactType =
      selectedContact.contactType &&
      ContactType[selectedContact.contactType]?.toLowerCase();
    return getVIESProviderLink(
      provider,
      shortCode,
      contactType,
      selectedContact.providerContactId
    );
  }, [selectedContact, providerName, shortCode]);

  const providerLogo = renderProviderLogo(providerName);

  const visibleFields: React.ReactNode[] = [];

  if (selectedContact) {
    visibleFields.push(
      <CardInfo
        key="name"
        label={t('common.name')}
        data={renderOrDash(selectedContact.name)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="email"
        label={t('signup.email-address')}
        data={renderOrDash(selectedContact.email)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="phone"
        label={t('contacts.phone')}
        data={renderOrDash(selectedContact.phone)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="taxNumber"
        label={t('main.tax-number')}
        data={renderOrDash(selectedContact.taxNumber)}
      />
    );

    visibleFields.push(
      <CardInfo
        key="contactType"
        label={t('contacts.contactType')}
        data={renderOrDash(
          selectedContact.contactType ? formatContactType(selectedContact) : undefined
        )}
      />
    );

    visibleFields.push(
      <CardInfo
        key="country"
        label={t('common.country')}
        data={renderOrDash(selectedContact.country)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="address"
        label={t('organisation.registered-address')}
        data={renderOrDash(selectedContact.address)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="address1"
        label={t('signup.address-line1')}
        data={renderOrDash(selectedContact.address1)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="zipCode"
        label={t('signup.zip-code')}
        data={renderOrDash(selectedContact.zipCode)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="mainContact"
        label={t('vat-checker.primary-contact')}
        data={renderOrDash(selectedContact.mainContact)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="mainContactEmail"
        label={t('vat-checker.primary-contact-email')}
        data={renderOrDash(selectedContact.mainContactEmail)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="otherContact"
        label={t('vat-checker.secondary-contact')}
        data={renderOrDash(selectedContact.otherContact)}
      />
    );
    visibleFields.push(
      <CardInfo
        key="otherContactEmail"
        label={t('vat-checker.secondary-contact-email')}
        data={renderOrDash(selectedContact.otherContactEmail)}
      />
    );
  }

  const midPoint = Math.ceil(visibleFields.length / 2);
  const leftColumnFields = useWideLayout
    ? visibleFields.slice(0, midPoint)
    : visibleFields;
  const rightColumnFields = useWideLayout ? visibleFields.slice(midPoint) : [];

  const columnStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    rowGap: 7,
    flex: useWideLayout ? 1 : undefined,
    minWidth: 0,
  };

  return (
    <StyledCard
      width={cardWidth}
      $padding="24px"
      style={{ marginTop: inlineBesideMain ? 0 : '20px', ...(style ?? {}) }}
    >
      <div style={{ overflow: 'visible' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
          }}
        >
          <Space style={{ columnGap: 10, alignItems: 'center' }}>
            <span style={{ height: '32px', display: 'block' }}>
              {providerContactHref ? (
                <a
                  href={providerContactHref}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: 'block', cursor: 'pointer' }}
                  aria-label={t('common.open-in-provider', {
                    provider: providerName || t('common.provider'),
                  })}
                >
                  {providerLogo}
                </a>
              ) : (
                providerLogo
              )}
            </span>

            <CardTitleSpan style={{ display: 'block', marginBottom: 0 }}>
              {t('organisation.contact-details-title')}
            </CardTitleSpan>
          </Space>

          {(windowWidth <= ENTITY_DETAILS_BREAKPOINT ||
            windowHeight <= ENTITY_DETAILS_HEIGHT_BREAKPOINT) && (
              <CloseOutlined
                role="button"
                tabIndex={0}
                aria-label={t('common.close')}
                onClick={onClose}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClose?.();
                  }
                }}
              />
            )}
        </div>
        <Divider style={{ margin: '16px 0' }} />
        {selectedContact && visibleFields.length > 0 ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '4px',
              flexDirection: useWideLayout ? 'row' : 'column',
              gap: useWideLayout ? 20 : 0,
              overflow: 'auto',
              maxWidth: fieldsMaxWidth,
              width: '100%',
            }}
          >
            <div style={columnStyle}>
              {leftColumnFields}
            </div>
            {useWideLayout && rightColumnFields.length > 0 && (
              <div style={columnStyle}>
                {rightColumnFields}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </StyledCard>
  );
};

